import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, AlertTriangle, RotateCcw } from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function AdminMaintenance() {
  const { user } = useAuth();
  const isSuperAdmin = user?.permissions?.includes("system:admin");
  const [events, setEvents] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const [resetMode, setResetMode] = useState(null);
  const [resetText, setResetText] = useState("");
  const [gOpen, setGOpen] = useState(false);
  const [gReason, setGReason] = useState("");
  const [gConfirm, setGConfirm] = useState("");

  const load = () => api.get("/admin/events").then((r) => setEvents(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));
  const ids = Object.keys(sel).filter((k) => sel[k]);

  const cleanup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-events", { event_ids: ids });
      toast.success(`Removidos: ${data.deleted.events} eventos, ${data.deleted.applications} candidaturas.`);
      setSel({}); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doReset = async () => {
    try {
      const { data } = await api.post("/admin/reset", { mode: resetMode, confirm: resetText });
      toast.success(`Reset ${data.mode === "full" ? "completo" : "operacional"} concluído.`);
      setResetMode(null); setResetText(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doGlobalReset = async () => {
    try {
      const { data } = await api.post("/admin/global-reset", { reason: gReason, confirm: gConfirm });
      toast.success(`Tudo zerado. ${data.usuarios_mantidos} cadastro(s) de usuário preservado(s).`);
      setGOpen(false); setGReason(""); setGConfirm(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-maintenance">
      <div className="flex items-start gap-3 p-4 rounded-2xl border border-amber-500/30 bg-amber-500/5">
        <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-sm text-amber-200/90">Ferramentas de limpeza de dados de teste. Registros financeiros reais não devem ser removidos por aqui. Ação permanente.</p>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{ids.length} selecionado(s)</span>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="cleanup-btn" disabled={ids.length === 0 || busy} className="rounded-xl bg-red-600 hover:bg-red-700 gap-2"><Trash2 className="h-4 w-4" />Limpar dados de teste</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-card border-white/10 rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir dados de teste?</AlertDialogTitle>
              <AlertDialogDescription>Esta ação excluirá permanentemente {ids.length} evento(s) e seus históricos de teste. Deseja continuar?</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl bg-transparent border-white/10">Voltar</AlertDialogCancel>
              <AlertDialogAction data-testid="cleanup-confirm-btn" onClick={cleanup} className="rounded-xl bg-red-600 hover:bg-red-700">Excluir permanentemente</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {events.length === 0 ? (
        <div className="text-muted-foreground bg-card border border-dashed border-white/10 rounded-2xl p-12 text-center">Nenhum evento cadastrado.</div>
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-auto">
          {events.map((e) => (
            <div key={e.id} data-testid={`admin-event-${e.id}`} className="flex items-center gap-4 bg-card border border-white/10 rounded-2xl p-4">
              <Checkbox data-testid={`admin-check-${e.id}`} checked={!!sel[e.id]} onCheckedChange={() => toggle(e.id)} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{e.name}</div>
                <div className="text-sm text-muted-foreground font-mono">{formatDate(e.date)} · {e.creator_name} · {e.status}</div>
              </div>
              <div className="text-xs text-muted-foreground shrink-0 text-right"><div>{e.applications} candidatura(s)</div></div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-6 border-t border-white/10" data-testid="reset-section">
        <h2 className="font-head text-xl font-semibold flex items-center gap-2"><RotateCcw className="h-5 w-5 text-red-400" />Resetar Dados de Ambiente</h2>
        <p className="text-muted-foreground text-sm mt-1 mb-4">Reinicie o ambiente de teste. Sua conta admin é preservada.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button data-testid="reset-operational-btn" onClick={() => { setResetMode("operational"); setResetText(""); }} className="text-left bg-card border border-amber-500/30 rounded-2xl p-4 hover:border-amber-500/60 transition-colors">
            <div className="font-medium text-amber-300">Resetar dados operacionais</div>
            <div className="text-xs text-muted-foreground mt-1">Apaga eventos, candidaturas, check-ins, pagamentos e notificações. Mantém usuários e repertório.</div>
          </button>
          <button data-testid="reset-full-btn" onClick={() => { setResetMode("full"); setResetText(""); }} className="text-left bg-card border border-red-500/30 rounded-2xl p-4 hover:border-red-500/60 transition-colors">
            <div className="font-medium text-red-400">Resetar ambiente completo</div>
            <div className="text-xs text-muted-foreground mt-1">Tudo do operacional + usuários (exceto admin), perfis e repertório.</div>
          </button>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="pt-6 border-t border-red-500/20" data-testid="global-reset-section">
          <h2 className="font-head text-xl font-semibold flex items-center gap-2 text-red-500"><AlertTriangle className="h-5 w-5" />Zona de perigo — Super Admin</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-4">Zera TODO o financeiro (movimentações, a receber, recebidos, GMV e receita acumulada — inclusive registros PAGOS) e todos os dados operacionais. Os cadastros de usuários são mantidos. Irreversível.</p>
          <button data-testid="global-reset-open" onClick={() => { setGOpen(true); setGReason(""); setGConfirm(""); }} className="text-left bg-red-500/5 border border-red-500/40 rounded-2xl p-4 hover:border-red-500/70 transition-colors w-full sm:w-auto">
            <div className="font-medium text-red-400 flex items-center gap-2"><Trash2 className="h-4 w-4" />Zerar tudo (Super Admin)</div>
            <div className="text-xs text-muted-foreground mt-1">Financeiro global + eventos + candidaturas + notificações. Mantém cadastros de usuários.</div>
          </button>
        </div>
      )}

      <Dialog open={gOpen} onOpenChange={(o) => !o && setGOpen(false)}>
        <DialogContent className="bg-card border-red-500/30 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-500">Zerar TUDO — começar do zero</DialogTitle>
            <DialogDescription>Esta ação apaga permanentemente todo o financeiro (inclusive PAGOS: cobranças, repasses e ledger), eventos, candidaturas, notificações e avaliações. Os cadastros de usuários são preservados. NÃO pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="global-reset-reason" value={gReason} onChange={(e) => setGReason(e.target.value)} placeholder="Motivo *" className="bg-[#09090B] border-white/10 rounded-xl" />
            <label className="text-sm text-zinc-300 block">Digite <span className="font-bold text-red-500">ZERAR TUDO</span> para confirmar:</label>
            <Input data-testid="global-reset-confirm" value={gConfirm} onChange={(e) => setGConfirm(e.target.value)} placeholder="ZERAR TUDO" className="bg-[#09090B] border-white/10 rounded-xl" />
            <Button data-testid="global-reset-execute" onClick={doGlobalReset} disabled={gConfirm !== "ZERAR TUDO" || !gReason.trim()} className="w-full bg-red-600 hover:bg-red-700 rounded-xl disabled:opacity-40 gap-1"><Trash2 className="h-4 w-4" />Zerar tudo agora</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetMode} onOpenChange={(o) => !o && setResetMode(null)}>
        <DialogContent className="bg-card border-white/10 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reset {resetMode === "full" ? "completo" : "operacional"}</DialogTitle>
            <DialogDescription>ATENÇÃO: exclusão permanente. Não pode ser desfeita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm text-zinc-300">Digite <span className="font-bold text-red-400">RESET</span> para confirmar:</label>
            <Input data-testid="reset-confirm-input" value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET" className="bg-[#09090B] border-white/10 rounded-xl" />
            <Button data-testid="reset-confirm-btn" onClick={doReset} disabled={resetText !== "RESET"} className="w-full bg-red-600 hover:bg-red-700 rounded-xl disabled:opacity-40 gap-1"><RotateCcw className="h-4 w-4" />Executar reset</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
